import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import KeyStore from 'utils/KeyStore';
import { useTheme } from '@material-ui/core/styles';
import {
    makeStyles,
    Tooltip,
    Checkbox,
    useMediaQuery,
} from '@material-ui/core';
import ModalUtils from 'utils/ModalUtils';
import { modules } from 'utils/enum/modules';
import StringUtils from 'lib/StringUtils';
import DateUtils, { DateFormat } from 'lib/DateUtils';
import { useQuery, useMutation } from '@apollo/client';
import { FetchPolicy, DataSort } from 'utils/enum/Core';
import { ActivityType } from 'utils/enum/ActivitiesEnum';
import ActivitiesQuery from 'services/graphQL/query/crm/ActivitiesQuery';
import ActivityMutation from 'services/graphQL/mutate/ActivityMutation';
import useAssigneeByLots from 'components/hook/crm/useAssigneeByLots';
import VirtualTable from 'components/widgets/VirtualTable';
import Select from 'components/widgets/Select';
import ConfirmDialog from 'components/widgets/modal/ConfirmDialog';
import InfiniteScroll from 'components/widgets/InfiniteScroll';
import clsx from 'clsx';

const useStyles = makeStyles((theme) => ({
    parent: {
        padding: '20px',
        '& > div:nth-child(1)': {
            width: '300px',
            [theme.breakpoints.down('sm')]: {
                width: '100%',
            },
        },
    },
    tableContainer: {
        width: '100%',
        height: '85vh',
        overflow: 'hidden',
        marginTop: '10px',
        '& > div': {
            overflowY: 'hidden',
            overflowX: 'auto',
            minWidth: '1200px',
        },
    },
    text: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    complete: {
        display: 'flex',
        justifyContent: 'center',
        [theme.breakpoints.down('sm')]: {
            justifyContent: 'right',
        },
    },
    scroller: {
        width: '100%',
        marginTop: '20px',
        marginBottom: '10px',
        height: '80vh',
        overflowX: 'hidden',
    },
    circularProgressContainer: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        '& > div': {
            width: '30px !important',
            height: '30px !important',
        },
    },
    activityParent: {
        borderBottom: `1px solid ${theme.palette.border.ghost}`,
        marginBottom: '10px',
    },
    activityRow: {
        display: 'flex',
        justifyContent: 'space-between',
        paddingRight: '10px',
        marginBottom: '10px',
        '& > div, & > span': {
            fontSize: '14px',
        },
        '& > div:nth-child(1)': {
            fontWeight: 'bold',
        },
        '& > div:nth-child(2)': {
            width: '200px',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
            textAlign: 'right',
        },
    },
}));

const ActivityElement = ({
    activity,
    assignees,
    onOpenCompleteDialog,
}) => {
    const classes = useStyles();
    const {
        processRecordId,
        realizationDate,
        note,
        assignee,
        opportunity: {
            leadCode,
            processStage,
            vehicleYear,
            vehicleMake,
            vehicleModel,
            vehicleTrim,
            prospect: {
                firstName,
                lastName,
                cellPhone,
                workPhone,
                homePhone,
            },
            vehicle,
        },
    } = activity;

    const {
        stockNumber,
        year,
        make,
        model,
        trim,
    } = vehicle ?? {};

    const nonStockVehicle = Number(vehicleYear) > 0 ? `${vehicleYear} ${vehicleMake ?? ''} ${vehicleModel ?? ''} ${vehicleTrim ?? ''}`.trim() : '';
    return (
        <div className={classes.activityParent}>
            <div>
                <div className={classes.activityRow}>
                    <div>Opportunity Code:</div>
                    <div>
                        <a target="_blank" rel="noopener noreferrer" href={`/${modules.OPPORTUNITIES}/${processRecordId}`}>
                            {leadCode}
                        </a>
                    </div>
                </div>
                <div className={classes.activityRow}>
                    <div>Prospect:</div>
                    <div>
                        {`${firstName} ${lastName ?? ''}`}
                    </div>
                </div>
                <div className={classes.activityRow}>
                    <div>Vehicle of Interest:</div>
                    <div>
                        {
                            Number(stockNumber ?? 0) > 0 && !StringUtils.isEmpty(make)
                                ? `${stockNumber} | ${year} ${make} ${model} ${trim}`
                                : nonStockVehicle
                        }
                    </div>
                </div>
                <div className={classes.activityRow}>
                    <div>Assignee:</div>
                    <div>
                        {(assignees ?? []).find((a) => a.value === assignee)?.label ?? ''}
                    </div>
                </div>
                <div className={classes.activityRow}>
                    <div>Cell Phone:</div>
                    <div>
                        {cellPhone ?? workPhone ?? homePhone}
                    </div>
                </div>
                <div className={classes.activityRow}>
                    <div>Process Stage:</div>
                    <div>
                        {processStage}
                    </div>
                </div>
                <div className={classes.activityRow}>
                    <div>Scheduled On:</div>
                    <div>
                        {DateUtils.format(realizationDate, DateFormat.DATETIME_WITHOUT_SECONDS)}
                    </div>
                </div>
                <div className={classes.activityRow}>
                    <div>Complete:</div>
                    <div>
                        <div className={classes.complete}>
                            <Checkbox
                                color="primary"
                                value="selected"
                                name="complete"
                                style={{ padding: 0 }}
                                checked={false}
                                onChange={() => onOpenCompleteDialog(activity)}
                                inputProps={{
                                    'aria-label': 'primary checkbox',
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <div className={classes.activityRow}>
                {`Note: ${note}`}
            </div>
        </div>
    );
};

const MessageCenterActivities = ({
    type,
    lots,
}) => {
    const classes = useStyles();
    const keyStore = new KeyStore();
    const { assignee } = useAssigneeByLots({ notIncludeEmptyValue: true, lots: lots.map((l) => l.id) });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [state, setState] = useState({
        activities: [],
        assignee: null,
        assignees: null,
        selectedRecord: null,
        showCompletePopup: false,
        tableWidth: 0,
        params: {
            paginate: {
                start: 0,
                limit: 50,
            },
            sort: {
                field: 'realizationDate',
                dir: DataSort.DESC,
            },
        },
    });

    const {
        activities,
        selectedRecord,
        showCompletePopup,
        tableWidth,
        params: {
            paginate,
            sort,
        },
    } = state;

    const isCallActivity = type === ActivityType.CALL;
    const {
        data, error, loading,
    } = useQuery(isCallActivity ? ActivitiesQuery.GET_CALL_ACTIVITIES_MESSAGE_CENTER : ActivitiesQuery.GET_TASK_ACTIVITIES_MESSAGE_CENTER,
        {
            variables: {
                type,
                assignee: state.assignee,
                paginate,
                sort,
            },
            fetchPolicy: FetchPolicy.NO_CACHE,
            skip: state.assignees == null,
        });

    const [completeActivity, { loading: completingActivity }] = useMutation(ActivityMutation.COMPLETE_ACTIVITY, {
        onCompleted: (response) => {
            if (response) {
                setState((prevState) => ({
                    ...prevState,
                    activities: prevState.activities.filter((a) => a.activityId !== selectedRecord.activityId),
                    selectedRecord: null,
                    showCompletePopup: false,
                }));

                ModalUtils.successMessage(null, 'Activity updated successfully');
            }
        },
        onError: (err) => {
            setState((prevState) => ({
                ...prevState,
                selectedRecord: null,
                showCompletePopup: false,
            }));

            ModalUtils.errorMessage(null, err);
        },
    });

    useEffect(() => {
        if (assignee.length === 0) return;

        const currentUserId = keyStore.getUserId();
        setState((prevState) => ({
            ...prevState,
            assignees: assignee,
            assignee: (assignee ?? []).find((a) => a.value === currentUserId)?.value,
        }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignee]);

    useEffect(() => {
        if (error) {
            ModalUtils.errorMessage(error?.graphQLErrors);
        }

        if (!loading && data) {
            const response = data[isCallActivity ? 'getCallActivitiesForMessageCenter' : 'getTaskActivitiesForMessageCenter'];
            if (Array.isArray(response)) {
                setState((prevState) => ({
                    ...prevState,
                    activities: [...prevState.activities, ...response],
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, error]);

    const onChange = (field, value) => {
        setState((prevState) => ({
            ...prevState,
            [field]: value,
            ...(field === 'assignee' ? {
                activities: [],
                params: {
                    ...prevState.params,
                    paginate: {
                        start: 0,
                        limit: 50,
                    },
                },
            } : {}),
        }));
    };

    const onSort = ({ sortBy, sortDirection }) => {
        setState((prevState) => ({
            ...prevState,
            activities: [],
            params: {
                ...prevState.params,
                paginate: {
                    start: 0,
                    limit: 50,
                },
                sort: {
                    field: sortBy,
                    dir: sortDirection,
                },
            },
        }));
    };

    const loadMore = () => {
        const currentOffset = activities?.length || 0;

        setState((prevState) => ({
            ...prevState,
            params: {
                ...prevState.params,
                paginate: {
                    start: currentOffset,
                    limit: 50,
                },
            },
        }));
    };

    const onOpenCompleteDialog = (record) => {
        setState((prevState) => ({
            ...prevState,
            selectedRecord: record,
            showCompletePopup: true,
        }));
    };

    const onCloseDialog = () => {
        setState((prevState) => ({
            ...prevState,
            selectedRecord: null,
            showCompletePopup: false,
        }));
    };

    const onCompleteActivity = () => {
        completeActivity({
            variables: {
                activityId: selectedRecord.activityId,
                processRecordId: selectedRecord.processRecordId,
            },
        });
    };

    const columns = [
        {
            label: 'Opportunity Code',
            dataKey: 'processRecordId',
            width: 150,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <a target="_blank" rel="noopener noreferrer" href={`/${modules.OPPORTUNITIES}/${record.processRecordId}`}>{record.opportunity.leadCode}</a>
                );
            },
        },
        {
            label: 'Prospect',
            dataKey: 'prospect',
            width: 300,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const { firstName, lastName } = record.opportunity.prospect;

                return (
                    <span className={classes.text}>{`${firstName} ${lastName ?? ''}`}</span>
                );
            },
        },
        {
            label: 'Cell Phone',
            dataKey: 'cellPhone',
            width: 150,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const { cellPhone, workPhone, homePhone } = record.opportunity.prospect;

                return (
                    <span className={classes.text}>{cellPhone ?? workPhone ?? homePhone}</span>
                );
            },
        },
        {
            label: 'Vehicle of Interest',
            dataKey: 'vehicle',
            width: 300,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                const {
                    vehicleYear,
                    vehicleMake,
                    vehicleModel,
                    vehicleTrim,
                } = record.opportunity;

                const {
                    stockNumber,
                    year,
                    make,
                    model,
                    trim,
                } = record.opportunity.vehicle ?? {};

                const nonStockVehicle = Number(vehicleYear) > 0 ? `${vehicleYear} ${vehicleMake ?? ''} ${vehicleModel ?? ''} ${vehicleTrim ?? ''}`.trim() : '';
                return (
                    <span className={classes.text}>
                        {
                            Number(stockNumber ?? 0) > 0 && !StringUtils.isEmpty(make)
                                ? `${stockNumber} | ${year} ${make} ${model} ${trim}`
                                : nonStockVehicle
                        }
                    </span>
                );
            },
        },
        {
            label: 'Process Stage',
            dataKey: 'processStage',
            width: 150,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{record.opportunity.processStage}</span>
                );
            },
        },
        {
            label: 'Scheduled On',
            dataKey: 'realizationDate',
            width: 150,
            disableSort: false,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;

                return (
                    <span className={classes.text}>{DateUtils.format(record.realizationDate, DateFormat.DATETIME_WITHOUT_SECONDS)}</span>
                );
            },
        },
        {
            label: 'Notes',
            dataKey: 'note',
            width: 200,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;

                return (
                    <Tooltip placement="top-start" title={record.note}>
                        <span className={classes.text}>
                            {record.note}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            label: 'Assignee',
            dataKey: 'assignee',
            width: 300,
            disableSort: true,
            cellRenderer: (cell) => {
                const { rowData: record } = cell;
                return (
                    <span className={classes.text}>{(state.assignees ?? []).find((a) => a.value === record.assignee)?.label ?? ''}</span>
                );
            },
        },
        {
            label: 'Complete',
            dataKey: 'action',
            width: 100,
            disableSort: true,
            cellRenderer: (cell) => (
                <div className={classes.complete}>
                    <Checkbox
                        color="primary"
                        value="selected"
                        name="complete"
                        style={{ padding: 0 }}
                        checked={false}
                        onChange={() => onOpenCompleteDialog(cell.rowData)}
                        inputProps={{
                            'aria-label': 'primary checkbox',
                        }}
                    />
                </div>
            ),
        },
    ];

    const clientWidth = document.getElementById('parent-container')?.clientWidth;
    useEffect(() => {
        if (clientWidth > 0 && tableWidth === 0) {
            const offsetInPixels = 50;
            const columnsWidth = columns.reduce((a, b) => a + b.width, 0);

            setState((prevState) => ({
                ...prevState,
                tableWidth: clientWidth > tableWidth ? clientWidth - offsetInPixels : columnsWidth,
            }));
        }
    }, [clientWidth, tableWidth, columns]);

    return (
        <>
            <div id="parent-container" className={classes.parent}>
                <Select
                    name="assignee"
                    onChange={(name, newValue) => onChange(name, newValue)}
                    value={state.assignee ?? ''}
                    options={[
                        { label: 'All Assignees', value: null },
                        ...(state.assignees ?? []).map((a) => ({ value: a.value, label: `Assignee: ${a.label}` })),
                    ]}
                    nowrap
                    size="sm"
                    loading={false}
                />
                {!isMobile && (
                    <div className={clsx(classes.tableContainer, 'virtual-table-base')}>
                        <VirtualTable
                            loading={loading}
                            loadMore={loadMore}
                            rowHeight={50}
                            totalRecords={Infinity}
                            data={activities}
                            columns={columns}
                            sort={onSort}
                            sortBy={sort.field}
                            sortDirection={sort.dir}
                            width={tableWidth || 0}
                        />
                    </div>
                )}
                {isMobile && (
                    <div className={classes.scroller}>
                        <InfiniteScroll
                            load={loading}
                            loadMore={loadMore}
                            loadAtScrollPercent={90}
                            totalRecord={Infinity}
                            lengthRecord={(state.assignees ?? []).length}
                        >
                            {activities.map((a, index) => (
                                <ActivityElement
                                    key={index}
                                    activity={a}
                                    assignees={state.assignees ?? []}
                                    onOpenCompleteDialog={onOpenCompleteDialog}
                                />
                            ))}
                        </InfiniteScroll>
                    </div>
                )}
            </div>
            {showCompletePopup && selectedRecord && (
                <ConfirmDialog
                    open
                    title="Activity"
                    description={`Are you sure you want to complete this activity for lead ${selectedRecord.opportunity.leadCode}?`}
                    variant="outlined"
                    titlePrimary="Yes"
                    titleSecondary="Cancel"
                    onClose={onCloseDialog}
                    onClickSecondary={onCloseDialog}
                    onClickPrimary={onCompleteActivity}
                    disablePrimaryButton={completingActivity}
                    disableSecondaryButton={completingActivity}
                />
            )}
        </>
    );
};

ActivityElement.propTypes = {
    activity: PropTypes.object.isRequired,
    assignees: PropTypes.array.isRequired,
    onOpenCompleteDialog: PropTypes.func.isRequired,
};

MessageCenterActivities.propTypes = {
    type: PropTypes.string.isRequired,
    lots: PropTypes.array.isRequired,
};

export default MessageCenterActivities;
